import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { feedAPI, resolveImageUrl } from '@/lib/api';
import { VerifiedBadge } from '@/components/professional/VerifiedBadge';
import { toast } from 'sonner';

const initials = (n = '') => n.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

export const PostComments = ({ postId, onAdded }) => {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    feedAPI.getComments(postId).then((r) => setComments(r.data)).catch(() => setComments([]));
  }, [postId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await feedAPI.commentPost(postId, text.trim());
      setComments((c) => [...(c || []), data]);
      setText('');
      onAdded?.();
    } catch { toast.error('Não foi possível comentar agora'); } finally { setSending(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t space-y-3" data-testid="post-comments-section">
      {comments === null ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : comments.map((c) => (
        <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
          <Avatar className="h-8 w-8"><AvatarImage src={resolveImageUrl(c.user_avatar)} /><AvatarFallback className="text-xs">{initials(c.user_name)}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0 rounded-xl bg-muted/50 px-3 py-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold" data-testid="comment-author-name">{c.user_name}</span>
              <VerifiedBadge show={c.user_professional_verified} compact testid="comment-verified-badge" />
              <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: ptBR })}</span>
            </div>
            <p className="text-sm break-words whitespace-pre-line">{c.content}</p>
          </div>
        </div>
      ))}
      <form onSubmit={send} className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Escreva um comentário acolhedor..." aria-label="Escreva um comentário" className="min-h-[44px]" data-testid="comment-input" />
        <Button type="submit" size="icon" disabled={sending || !text.trim()} className="min-h-[44px] min-w-[44px]" aria-label="Enviar comentário" data-testid="comment-submit">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
};
